import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { analyticsApi } from '@/api';
import {
  Alert,
  Badge,
  Card,
  CardHeader,
  ErrorState,
  LoadingState,
  PageHeader,
  StatTile,
} from '@/components/ui';
import { ExportMenu } from '@/features/analytics/ExportMenu';
import { PeriodPicker } from '@/features/analytics/PeriodPicker';
import { ReportTable } from '@/features/analytics/ReportTable';
import { count, percent } from '@/features/analytics/format';
import { errorMessage } from '@/hooks/useApiError';

const PARSE_LABELS = {
  READY: 'Read successfully',
  FAILED: 'Could not be read',
  PENDING: 'Waiting to be read',
};

/** Plain names for the score ranges, best first in the table. */
const BAND_NAMES = {
  '80-100': 'Excellent match',
  '60-79': 'Good match',
  '40-59': 'Fair match',
  '20-39': 'Weak match',
  '0-19': 'Poor match',
};

/** Hired as a share of applications, or null when there were none. */
function hireRate(rows) {
  const applications = rows.reduce((sum, row) => sum + row.applications, 0);
  const hired = rows.reduce((sum, row) => sum + row.hired, 0);
  return applications ? Math.round((hired / applications) * 1000) / 10 : null;
}

/** The answer in one sentence: higher scores against lower ones. */
function ScoreVerdict({ bands, scored, thin }) {
  if (!bands?.length || scored === 0) return null;
  const lowerBound = (row) => Number(String(row.band).split('-')[0]);
  const high = hireRate(bands.filter((row) => lowerBound(row) >= 60));
  const low = hireRate(bands.filter((row) => lowerBound(row) < 60));

  let tone = 'info';
  let title = 'No clear pattern yet';
  let text = 'There are not enough hires on both sides to compare higher and lower scores.';
  if (high != null && low != null) {
    if (high > low) {
      tone = 'success';
      title = 'Higher scores are being hired more often';
      text = `Candidates scoring 60 or more were hired ${percent(high)} of the time, against ${percent(low)} for lower scores.`;
    } else {
      tone = 'warning';
      title = 'Higher scores are not being hired more often';
      text = `Candidates scoring 60 or more were hired ${percent(high)} of the time, against ${percent(low)} for lower scores. If this holds over a longer period, the scoring rules need a review.`;
    }
  }

  return (
    <Alert
      tone={thin ? 'warning' : tone}
      className="mb-4"
      title={thin ? `Early sign: ${title.toLowerCase()}` : title}
    >
      {text}
      {thin &&
        ` This is based on only ${count(scored)} scored applications, too few to rely on. Choose a longer period for a dependable answer.`}
    </Alert>
  );
}

/** One row per score range, best first, with the hire rate drawn as a bar. */
function ScoreBands({ bands }) {
  if (!bands?.length) {
    return <p className="text-ink-muted text-sm">Nothing has been scored in this period.</p>;
  }
  const rows = [...bands].reverse();
  return (
    <div>
      <div className="text-ink-muted grid grid-cols-[minmax(0,1.4fr)_4.5rem_3.5rem_minmax(0,1.6fr)] gap-3 border-b border-line pb-2 text-xs font-medium tracking-wide uppercase">
        <span>Match score</span>
        <span className="text-right">Applied</span>
        <span className="text-right">Hired</span>
        <span>Hire rate</span>
      </div>
      <ul className="divide-line divide-y">
        {rows.map((row) => (
          <li
            key={row.band}
            className="grid grid-cols-[minmax(0,1.4fr)_4.5rem_3.5rem_minmax(0,1.6fr)] items-center gap-3 py-3 text-sm"
          >
            <span className="min-w-0">
              <span className="text-ink block font-medium">{BAND_NAMES[row.band] ?? row.band}</span>
              <span className="text-ink-muted text-xs">Score {row.band.replace('-', '–')}</span>
            </span>
            <span className="text-ink text-right tabular-nums">{count(row.applications)}</span>
            <span className="text-ink text-right tabular-nums">{count(row.hired)}</span>
            <span className="flex min-w-0 items-center gap-2">
              <span className="bg-surface-muted h-2 min-w-0 flex-1 overflow-hidden rounded-full">
                {row.hire_rate != null && (
                  <span
                    className="bg-brand-500 block h-full rounded-full"
                    style={{ width: `${Math.max(row.hire_rate, row.hire_rate > 0 ? 4 : 0)}%` }}
                  />
                )}
              </span>
              <span className="text-ink w-12 shrink-0 text-right text-xs font-semibold tabular-nums">
                {percent(row.hire_rate)}
              </span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

const EXPORT_SHEETS = [
  { key: 'ai-bands', label: 'Score bands', hint: 'Applications and hires per band' },
  { key: 'ai-parsing', label: 'CV parsing', hint: 'Outcomes over the period' },
];

/**
 * What the AI features are doing, and whether the match score means anything.
 *
 * Nothing on this page probes a vendor: it reports what is configured and what
 * the data shows. Whether OpenAI is answering right now is a liveness question
 * and belongs to the health check — a report that calls out in order to render
 * is a report that hangs when the vendor does.
 */
export function AdminAiReportPage() {
  const [period, setPeriod] = useState(90);

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['platform-analytics', 'ai', period],
    queryFn: () => analyticsApi.getAiReport(period),
  });

  if (isPending) return <LoadingState label="Reading the pipeline…" />;
  if (isError) {
    return <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />;
  }

  const { provider, coverage, parsing } = data;
  // A band chart drawn over a handful of applications is noise wearing the
  // shape of a finding, so the caveat is attached to the chart rather than
  // left for the reader to work out.
  const thin = coverage.scored < 20;

  return (
    <div>
      <PageHeader
        title="AI usage"
        description="How the AI features are set up, how much they are used, and whether the match score is helping companies hire."
        action={
          <div className="flex flex-wrap items-center gap-2">
            <PeriodPicker value={period} onChange={setPeriod} />
            <ExportMenu sheets={EXPORT_SHEETS} days={period} />
          </div>
        }
      />

      <Card className="mb-6">
        <CardHeader
          title="Setup"
          description="The AI services this platform is set up to use. To check they are responding right now, use the system health page."
        />
        <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <dt className="text-ink-muted text-xs">Provider</dt>
            <dd className="mt-1">
              {provider.ai_configured ? (
                <Badge tone="success">{provider.ai_provider ?? 'Configured'}</Badge>
              ) : (
                <Badge tone="neutral">Not configured</Badge>
              )}
            </dd>
          </div>
          <div>
            <dt className="text-ink-muted text-xs">Embedding model</dt>
            <dd className="text-ink mt-1 font-mono text-xs">{provider.embedding_model || '—'}</dd>
          </div>
          <div>
            <dt className="text-ink-muted text-xs">Vector store</dt>
            <dd className="mt-1">
              {provider.vector_store_configured ? (
                <Badge tone="success">Configured</Badge>
              ) : (
                <Badge tone="neutral">Not configured</Badge>
              )}
            </dd>
          </div>
          <div>
            <dt className="text-ink-muted text-xs">Would be indexed</dt>
            <dd className="text-ink mt-1 text-sm tabular-nums">
              {count(data.indexable?.students)} students · {count(data.indexable?.internships)}{' '}
              listings
            </dd>
          </div>
        </dl>
        {/* The index is behind an interface with no count, and claiming to know
            what the vendor holds would be a number nobody could check. */}
        <p className="text-ink-muted mt-4 text-xs">
          &ldquo;Would be indexed&rdquo; is how many profiles and listings are eligible for AI
          search — the size of a full reindex. It is not read from the vector store itself.
        </p>
      </Card>

      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile
          label="Applications scored"
          value={percent(coverage.coverage_rate)}
          hint={`${count(coverage.scored)} of ${count(coverage.applications)} in this period`}
        />
        <StatTile
          label="Median score"
          value={coverage.median_score == null ? '—' : coverage.median_score}
          hint="The middle score across all scored applications"
        />
        <StatTile
          label="CVs read"
          value={percent(parsing.success_rate)}
          hint={`${count(parsing.attempted)} attempted · ${count(parsing.uploads)} uploaded`}
        />
        <StatTile
          label="Companies seeing scores"
          value={count(data.companies_with_scored_applications)}
          hint="Companies with at least one scored applicant"
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader
            title="Is the match score working?"
            description="Each application gets a match score when the student applies. If the score is useful, candidates with higher scores should be hired more often."
          />
          <ScoreVerdict bands={data.bands} scored={coverage.scored} thin={thin} />
          <ScoreBands bands={data.bands} />
          <p className="text-ink-muted mt-4 text-xs leading-5">
            The score is shown to companies as guidance only. Nothing on the platform hides, ranks
            or rejects a candidate because of it. “—” means no applications fell in that range, so
            there is nothing to measure.
          </p>
        </Card>

        <Card>
          <CardHeader
            title="Reading uploaded CVs"
            description="When a student uploads a CV, the platform reads it to suggest profile details. These are the results for this period."
          />
          <ReportTable
            rows={Object.entries(parsing.by_status ?? {}).map(([status, value]) => ({
              status,
              value,
            }))}
            rowKey={(row) => row.status}
            empty="No CVs were parsed in this period"
            columns={[
              {
                key: 'status',
                header: 'Outcome',
                cell: (row) => PARSE_LABELS[row.status] ?? row.status,
              },
              {
                key: 'value',
                header: 'CVs',
                numeric: true,
                cell: (row) => count(row.value),
              },
            ]}
          />
          <p className="text-ink-muted mt-3 text-xs">
            {count(parsing.students_with_a_parsed_cv)} students have at least one CV that was read
            successfully.
          </p>
        </Card>
      </div>
    </div>
  );
}
